import { useMemo } from 'react';
import { Briefcase, X, RefreshCw } from 'lucide-react';
import { compareProjectRefs } from './projectSort';

/**
 * ProjectFilterSidebar — right-rail list of live projects.
 *
 *   • Coloured dot matches projectColorMap (so the rail and the shift cards
 *     share the same colour language).
 *   • Click a row → calendar filters to that project.
 *   • Click the active row again (or "All projects") → clear filter.
 *   • Shows the number of upcoming shifts in the current view window.
 *
 * The list is "live": Schedule.jsx re-fetches projects on focus and every
 * 60 s, so newly-created / archived projects appear without a hard reload.
 */
export const ProjectFilterSidebar = ({
  projects,
  schedules,
  projectColorMap,
  projectFilter,
  onSelectProject,
  onRefresh,
  refreshing = false,
  onProjectDragStart,
  onProjectDragEnd,
  canDragProjects = false,
}) => {
  // Count shifts per project within the currently-fetched window
  const shiftCounts = useMemo(() => {
    const map = new Map();
    for (const s of schedules) {
      map.set(s.project_id, (map.get(s.project_id) || 0) + 1);
    }
    return map;
  }, [schedules]);

  // Sort: grouped by alphabetic prefix (LC … LUX …), numerical within group.
  // Users want predictable navigation more than count-based ranking.
  const sortedProjects = useMemo(() => {
    return [...projects].sort(compareProjectRefs);
  }, [projects]);

  return (
    <aside
      className="hidden xl:flex flex-col w-[260px] flex-shrink-0 bg-[#2A3240] border border-white/10 rounded-lg overflow-hidden self-start"
      data-testid="project-filter-sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10 bg-[#1E2530]">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-[#F5A623]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-300">
            Live Projects
          </span>
          <span className="text-[10px] text-gray-500">({projects.length})</span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="p-1 text-gray-500 hover:text-[#F5A623] disabled:opacity-50 transition-colors"
          title="Refresh project list"
          data-testid="project-sidebar-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* "All projects" pill */}
      <button
        type="button"
        onClick={() => onSelectProject('all')}
        data-testid="project-sidebar-all-btn"
        className={`flex items-center justify-between px-3 py-2 border-b border-white/5 text-left transition-colors ${
          projectFilter === 'all'
            ? 'bg-[#F5A623]/10 text-[#F5A623]'
            : 'text-gray-300 hover:bg-white/5'
        }`}
      >
        <span className="text-xs font-medium">All projects</span>
        {projectFilter !== 'all' && <X className="w-3 h-3 text-gray-500" />}
      </button>

      {/* Project list */}
      <div className="flex-1 overflow-y-auto max-h-[60vh]">
        {sortedProjects.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-gray-500">
            No live projects.
          </div>
        ) : (
          sortedProjects.map((proj) => {
            const colors = projectColorMap[proj.id] || {};
            const isActive = projectFilter === proj.id;
            const count = shiftCounts.get(proj.id) || 0;

            // Split "LUX208-DEV-10 The Avenue, Northampton" → header + sub
            let header = proj.project_ref || '';
            let sub = '';
            const parts = (proj.project_ref || '').split('-');
            if (parts.length >= 3) {
              header = `${parts[0]} · ${parts[1]}`;
              sub = parts.slice(2).join('-').trim();
            }

            return (
              <div
                key={proj.id}
                role="button"
                tabIndex={0}
                draggable={canDragProjects}
                onDragStart={(e) => {
                  if (!canDragProjects || !onProjectDragStart) return;
                  // text/plain is required for the drag-and-drop spec to
                  // emit a valid dragstart in Firefox; without ANY data set,
                  // Firefox silently cancels. We avoid effectAllowed='copy'
                  // because if the drop target doesn't echo dropEffect='copy'
                  // the browser shows a "not allowed" cursor and rejects the
                  // drop — that was the original symptom from the user.
                  try {
                    e.dataTransfer.setData('text/plain', proj.project_ref || proj.id);
                    e.dataTransfer.setData('application/x-lux-project', JSON.stringify({ id: proj.id, ref: proj.project_ref }));
                  } catch (_) {} // eslint-disable-line no-empty
                  // Leave effectAllowed at its default ('uninitialized'/'all')
                  // so any dropEffect on the target is accepted.
                  onProjectDragStart(proj);
                }}
                onDragEnd={() => onProjectDragEnd && onProjectDragEnd()}
                onClick={() => onSelectProject(isActive ? 'all' : proj.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectProject(isActive ? 'all' : proj.id);
                  }
                }}
                data-testid={`project-sidebar-row-${proj.id}`}
                className={`w-full flex items-start gap-2.5 px-3 py-2 text-left border-b border-white/5 transition-colors ${
                  isActive ? 'bg-[#F5A623]/10' : 'hover:bg-white/5'
                } ${canDragProjects ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
                title={canDragProjects ? 'Click to filter · Drag onto a day to create a shift' : 'Click to filter'}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full ${colors.dot || 'bg-gray-500'} flex-shrink-0 mt-1`}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className={`block text-xs font-semibold truncate ${isActive ? 'text-[#F5A623]' : 'text-white'}`}>
                    {header}
                  </span>
                  {sub && (
                    <span className="block text-[10px] text-gray-400 truncate">
                      {sub}
                    </span>
                  )}
                </span>
                {count > 0 && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      isActive
                        ? 'bg-[#F5A623]/20 text-[#F5A623]'
                        : 'bg-white/5 text-gray-400'
                    }`}
                    title={`${count} shifts in current view`}
                  >
                    {count}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};
